// src/pages/GuestsPeoplePage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { 
  PlusIcon, 
  PencilSquareIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  
} from "@heroicons/react/24/solid";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import AddPermanentGuestListModal from "../components/AddPermanentGuestListModal";
import EditPermanentGuestListModal from "../components/EditPermanentGuestListModal";
import ConfirmModal from "../components/ConfirmModal";
import { toast } from "react-toastify";
import Spinner from "../components/Spinner";
import { SERVER_BASE_URL } from "../config";



function GuestsPeoplePage() {
  const [permanentGuestLists, setPermanentGuestLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [listToEdit, setListToEdit] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);

  // Состояния сортировки
  const [sortBy, setSortBy] = useState("default");
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Состояние для поиска
  const [searchTerm, setSearchTerm] = useState("");
  
  // Состояние для отображения списков
  const [showLists, setShowLists] = useState(true);

  // Функция для определения, мобильное ли устройство
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  const fetchPermanentGuestLists = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load permanent guest lists");
      }

      const data = await response.json();
      setPermanentGuestLists(data);
    } catch (error) {
      console.error("Error loading permanent guest lists:", error);
      toast.error("Failed to load permanent guest lists.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermanentGuestLists();
  }, [fetchPermanentGuestLists]);

  // Фильтрация списков по поисковой строке
  const filteredLists = permanentGuestLists.filter((list) =>
    list.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Сортировка списков по выбранному столбцу
  const sortedPermanentGuestLists = filteredLists.slice().sort((a, b) => {
    let cmp = 0;
    if (sortBy === "default") {
      // Списки с is_default === true будут выше (при asc)
      cmp = a.is_default === b.is_default ? 0 : a.is_default ? -1 : 1;
    } else if (sortBy === "listName") {
      cmp = a.name.localeCompare(b.name);
    } else if (sortBy === "guests") {
      cmp = (a.guest_count || 0) - (b.guest_count || 0);
    }
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const handleAddList = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleEditList = (list) => {
    setListToEdit(list);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setListToEdit(null);
  };

  const handleDeleteList = async () => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${listToDelete}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete permanent guest list");
      }

      fetchPermanentGuestLists();
      setShowConfirmModal(false);
      setListToDelete(null);
      toast.success("Permanent guest list successfully deleted.");
    } catch (error) {
      console.error("Error deleting permanent guest list:", error);
      toast.error("Failed to delete permanent guest list.");
    }
  };

  const handleDeleteClick = (listId) => {
    setListToDelete(listId);
    setShowConfirmModal(true);
  };

  const handleCreateList = async (listData) => {
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/permanent-guest-lists/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(listData),
      });

      if (!response.ok) {
        throw new Error("Failed to create permanent guest list");
      }

      handleCloseAddModal();
      fetchPermanentGuestLists();
      toast.success("Permanent guest list successfully created.");
    } catch (error) {
      console.error("Error creating permanent guest list:", error);
      toast.error("Failed to create permanent guest list.");
    }
  };

  const handleUpdateList = async (updatedListData) => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${updatedListData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(updatedListData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update permanent guest list");
      }

      handleCloseEditModal();
      fetchPermanentGuestLists();
      toast.success("Permanent guest list successfully updated.");
    } catch (error) {
      console.error("Error updating permanent guest list:", error);
      toast.error("Failed to update permanent guest list.");
    }
  };

  const handleDefaultListChange = async (listId, isDefault) => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/${listId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ is_default: isDefault }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update permanent guest list default status");
      }

      fetchPermanentGuestLists();
      toast.success("Permanent guest list default status updated.");
    } catch (error) {
      console.error("Error updating permanent guest list default status:", error);
      toast.error("Failed to update permanent guest list default status.");
    }
  };

  // Bulk deletion handlers
  const handleCheckboxChangeList = (listId) => {
    setSelectedListIds((prev) =>
      prev.includes(listId) ? prev.filter((id) => id !== listId) : [...prev, listId]
    );
  };

  const handleSelectAllLists = () => {
    if (selectedListIds.length === filteredLists.length) {
      setSelectedListIds([]);
    } else {
      setSelectedListIds(filteredLists.map((list) => list.id));
    }
  };

  const handleDeleteSelectedLists = async () => {
    try {
      await Promise.all(
        selectedListIds.map((id) =>
          fetch(`${SERVER_BASE_URL}/api/permanent-guest-lists/${id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          })
        )
      );
      fetchPermanentGuestLists();
      setSelectedListIds([]);
      setShowBulkConfirmModal(false);
      toast.success("Selected permanent guest lists successfully deleted.");
    } catch (error) {
      console.error("Error deleting selected permanent guest lists:", error);
      toast.error("Failed to delete selected permanent guest lists.");
    }
  };

  const confirmBulkDelete = async () => {
    await handleDeleteSelectedLists();
  };

  // Обработчик сортировки: если кликнули по уже выбранной колонке – меняем порядок, иначе устанавливаем новую колонку
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return <span className="ml-1 text-gray-400">–</span>;
    }
    return sortOrder === "asc" ? (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▲
      </span>
    ) : (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▼
      </span>
    );
  };

  return (
    <div>
      {/* Карточка верхнего блока */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Permanent Guest Lists</h1>
        
        {/* Информационные карточки - Desktop view */}
        <div className="hidden sm:grid sm:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-lg -l-4 bg-indigo-50">
            <p className="text-sm text-gray-600 mb-1">Total Lists</p>
            <p className="text-2xl font-bold text-gray-800">{permanentGuestLists.length}</p>
          </div>
          
          <div className="p-4 rounded-lg  bg-indigo-50">
            <p className="text-sm text-gray-600 mb-1">Default Lists</p>
            <p className="text-2xl font-bold text-gray-800">
              {permanentGuestLists.filter(list => list.is_default).length}
            </p>
          </div>
          
          <div className="p-4 rounded-lg  bg-indigo-50">
            <p className="text-sm text-gray-600 mb-1">Total Guests</p>
            <p className="text-2xl font-bold text-gray-800">
              {permanentGuestLists.reduce((sum, list) => sum + (list.guest_count || 0), 0)}
            </p>
          </div>
        </div>

        {/* Mobile view - small square cards */}
        <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
          <div className="py-2 rounded-lg bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
            <p className="text-[10px] text-gray-600 m-0 text-center">Total Lists</p>
            <p className="text-lg font-bold text-gray-800 text-center m-0">
              {permanentGuestLists.length}
            </p>
          </div>

          <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
            <p className="text-[10px] text-gray-600 m-0 text-center">Default</p>
            <p className="text-lg font-bold text-gray-800 text-center m-0">
              {permanentGuestLists.filter(list => list.is_default).length}
            </p>
          </div>

          <div className="py-2 rounded-lg  bg-indigo-50 w-[30%] flex flex-col justify-center px-1">
            <p className="text-[10px] text-gray-600 m-0 text-center">Guests</p>
            <p className="text-lg font-bold text-gray-800 text-center m-0">
              {permanentGuestLists.reduce((sum, list) => sum + (list.guest_count || 0), 0)}
            </p>
          </div>
        </div>
        
        {/* Действия */}
        <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0">
          <div className="flex space-x-2">
            <button
              onClick={handleAddList}
              className="bg-indigo-600 text-white font-bold px-4 py-2 rounded flex items-center hover:bg-indigo-700 transition duration-200"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              Add New List
            </button>
            
            {selectedListIds.length > 0 && (
              <button
                onClick={() => setShowBulkConfirmModal(true)}
                className="flex items-center bg-white text-gray-600 px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition duration-200"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                ({selectedListIds.length})
              </button>
            )}
          </div>
          
          {/* Поиск */}
          <div className="relative max-w-md w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search lists..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Таблица списков - в отдельной карточке */}
      <div className="bg-white rounded-lg shadow-md p-6 ">
        <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto] items-center bg-gray-100 rounded-t-lg px-4 py-2">
          <div>
            <input
              type="checkbox"
              style={{ marginLeft: "5px" }}
              checked={selectedListIds.length === filteredLists.length && filteredLists.length > 0}
              onChange={handleSelectAllLists}
            />
          </div>
          <h3 className="font-semibold text-gray-600 whitespace-nowrap" style={{ fontSize: isMobile() ? '1rem' : '1.125rem' }}>
            Guest Lists
          </h3>
          <div></div>
          <div></div>
          <div className="flex justify-center">
            <button
              onClick={() => setShowLists((prev) => !prev)}
              className="focus:outline-none"
            >
              {showLists ? (
                <FiChevronUp className="text-xl" />
              ) : (
                <FiChevronDown className="text-xl" />
              )}
            </button>
          </div>
        </div>

        {showLists && (
          <>
            <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto] items-center font-semibold gap-2 border-b border-gray-200 py-1 px-4" style={{ fontSize: isMobile() ? '0.8rem' : '0.9rem' }}>
              <div></div>
              <div
                className="cursor-pointer"
                onClick={() => handleSort("default")}
              >
                Default
                {renderSortIcon("default")}
              </div>
              <div
                className="cursor-pointer"
                onClick={() => handleSort("listName")}
              >
                List Name
                {renderSortIcon("listName")}
              </div>
              <div
                className="cursor-pointer text-center"
                onClick={() => handleSort("guests")}
              >
                Guests
                {renderSortIcon("guests")}
              </div>
              <div className="text-center">Actions</div>
            </div>
            
            {loading ? (
              <div className="py-20 flex justify-center items-center">
                <Spinner />
              </div>
            ) : sortedPermanentGuestLists.length === 0 ? (
              <div className="py-10 text-center text-gray-500">
                {searchTerm ? "No lists match your search." : "No lists available."}
              </div>
            ) : (
              <div>
                {sortedPermanentGuestLists.map((list) => (
                  <div
                    key={list.id}
                    className="grid grid-cols-[40px_1fr_4fr_2fr_auto] items-center border-b border-indigo-100 divide-x divide-indigo-100 p-2 hover:bg-indigo-50 transition duration-200 cursor-default bg-indigo-50"
                  >
                    <div className="text-center">
                      <input
                        type="checkbox"
                        className="mx-auto"
                        checked={selectedListIds.includes(list.id)}
                        onChange={() => handleCheckboxChangeList(list.id)}
                      />
                    </div>
                    <div className="px-2 flex justify-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={list.is_default || false}
                          onChange={(e) => handleDefaultListChange(list.id, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 dark:bg-gray-700 peer-checked:bg-indigo-500"></div>
                        <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transform transition-transform duration-200 ease-in-out peer-checked:translate-x-[1.30rem]"></span>
                      </label>
                    </div>
                    <div className="text-base font-medium text-black px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                      <Link to={`/permanent-guest-lists/${list.id}`} className="block hover:text-indigo-600" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
                        {list.name}
                      </Link>
                    </div>
                    <div className="text-sm font-medium text-gray-600 text-center px-2">
                      {list.guest_count || 0}
                    </div>
                    <div className="flex space-x-2 justify-center px-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleEditList(list)}
                        className="text-gray-500 hover:text-yellow-700"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(list.id)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Пустое состояние, когда нет списков */}
      {!loading && permanentGuestLists.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center mt-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No guest lists found</h3>
          <p className="text-gray-500 mb-6">Create your first permanent guest list to get started.</p>
          <button
            onClick={handleAddList}
            className="bg-indigo-600 text-white font-bold px-4 py-2 rounded hover:bg-indigo-700 transition duration-200"
          >
            <PlusIcon className="inline-block h-5 w-5 mr-2" />
            Add New List
          </button>
        </div>
      )}

      {/* Modals */}
      <AddPermanentGuestListModal
        isOpen={showAddModal}
        onClose={handleCloseAddModal}
        onSave={handleCreateList}
      />

      {showEditModal && listToEdit && (
        <EditPermanentGuestListModal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onUpdate={handleUpdateList}
          listData={listToEdit}
        />
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleDeleteList}
        message="Are you sure you want to delete this permanent guest list?"
      />

      <ConfirmModal
        isOpen={showBulkConfirmModal}
        onClose={() => setShowBulkConfirmModal(false)}
        onConfirm={confirmBulkDelete}
        message="Are you sure you want to delete the selected permanent guest lists?"
      />
    </div>
  );
}

export default GuestsPeoplePage;